// Benchmarked on a Pi 5 (the floor for any user laptop): RFRK_OK ≤ 0.5 s everywhere; bagged EBK-KED
// ~0.2 s typical, ~8 s single-thread on a 640-ac worst case — hence `ebkKedDraws` is a GENERATOR
// so the caller can yield to the event loop between bags and keep the spinner honest. A PREVIEW
// ENGINE, NOT A SECOND SYSTEM OF RECORD.

/* * Exponential model: cov(h) = sill·exp(−h/lenScale); cov(0) adds the nugget (exact interpolation). */
export interface Variogram {
  sill: number;
  lenScale: number;
  nugget: number;
}

/* * Deterministic 32-bit PRNG — seeded, so a recreated surface is identical across reopens. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* * m distinct indices from 0..n-1 (partial Fisher–Yates) — numpy's rng.choice(replace=False) role. */
export function sampleWithoutReplacement(
  rnd: () => number,
  n: number,
  m: number,
): Int32Array {
  const pool = new Int32Array(n);
  for (let i = 0; i < n; i++) pool[i] = i;
  for (let i = 0; i < m; i++) {
    const j = i + Math.floor(rnd() * (n - i));
    const t = pool[i]!;
    pool[i] = pool[j]!;
    pool[j] = t;
  }
  return pool.slice(0, m);
}

// ---- dense LU with partial pivoting (systems are ≤ ~260×260 — samples + constraints) --------------
export function luFactor(
  A: Float64Array,
  n: number,
): { piv: Int32Array; singular: boolean } {
  const piv = new Int32Array(n);
  for (let i = 0; i < n; i++) piv[i] = i;
  // An absolute threshold misreads a legitimately small-sill system (near-flat residuals) as
  // singular — sill tracks the residual variance, which spans ~1e-2 (pH) to ~1e2 (P) across
  // properties.
  let scale = 0;
  for (let i = 0; i < n * n; i++) {
    const v = Math.abs(A[i]!);
    if (v > scale) scale = v;
  }
  const tiny = Math.max(scale, 1e-30) * 1e-13;
  let singular = false;
  for (let k = 0; k < n; k++) {
    let p = k;
    let max = Math.abs(A[k * n + k]!);
    for (let i = k + 1; i < n; i++) {
      const v = Math.abs(A[i * n + k]!);
      if (v > max) {
        max = v;
        p = i;
      }
    }
    if (max < tiny) singular = true; // near-zero pivot at this scale: degenerate, caller discards
    if (p !== k) {
      for (let j = 0; j < n; j++) {
        const t = A[k * n + j]!;
        A[k * n + j] = A[p * n + j]!;
        A[p * n + j] = t;
      }
      const t = piv[k]!;
      piv[k] = piv[p]!;
      piv[p] = t;
    }
    const d = A[k * n + k]! || 1e-12;
    for (let i = k + 1; i < n; i++) {
      const f = A[i * n + k]! / d;
      A[i * n + k] = f;
      for (let j = k + 1; j < n; j++) A[i * n + j] -= f * A[k * n + j]!;
    }
  }
  return { piv, singular };
}

export function luSolve(
  A: Float64Array,
  piv: Int32Array,
  n: number,
  b: Float64Array,
  x: Float64Array,
): void {
  for (let i = 0; i < n; i++) x[i] = b[piv[i]!]!;
  for (let i = 1; i < n; i++) {
    let s = x[i]!;
    for (let j = 0; j < i; j++) s -= A[i * n + j]! * x[j]!;
    x[i] = s;
  }
  for (let i = n - 1; i >= 0; i--) {
    let s = x[i]!;
    for (let j = i + 1; j < n; j++) s -= A[i * n + j]! * x[j]!;
    x[i] = s / (A[i * n + i]! || 1e-12);
  }
}

// ---- the robust variogram fit (compute's `_fit_variogram_robust`, deterministic-search flavor) ----
/*
 *
 * Exponential+nugget fit on values at metric coords. Sill pinned to the sample variance, len_scale
 * bounded to [mind/4, 1.5·diam], nugget to [0, var]; the moment estimate (0.7v / diam·⅓ / 0.3v) is
 * both the degenerate-input answer and the sanity fallback — exactly compute's ladder. The 6-bin
 * Matheron estimator mirrors `gs.vario_estimate(bin_edges=linspace(0, max(0.6·diam, 2·mind), 7))`.
 */
export function fitVariogramRobust(
  x: Float64Array,
  y: Float64Array,
  val: Float64Array,
): Variogram {
  const n = x.length;
  let mean = 0;
  for (let i = 0; i < n; i++) mean += val[i]! / n;
  let v = 0;
  for (let i = 0; i < n; i++) v += (val[i]! - mean) ** 2 / n; // np.var, ddof=0
  let diam = 0;
  let mind = Infinity;
  for (let i = 0; i < n; i++)
    for (let j = i + 1; j < n; j++) {
      const h = Math.sqrt((x[i]! - x[j]!) ** 2 + (y[i]! - y[j]!) ** 2);
      if (h > diam) diam = h;
      if (h > 0 && h < mind) mind = h;
    }
  if (!Number.isFinite(mind)) mind = 1;
  if (diam <= 0) diam = 1;
  // scipy's solver survives a 1e-15 sill; a float64 LU cannot (condition ~1e16 against the
  // constraint rows). For a zero-variance input ANY positive sill predicts identically — OK
  // reproduces a constant field and KED reproduces its drift for every valid model — so the floor
  // changes conditioning, never values.
  const floor = Math.max(1e-9 * (1 + mean * mean), 1e-12);
  const moment: Variogram = {
    sill: Math.max(0.7 * v, floor),
    lenScale: Math.max(diam / 3, mind),
    nugget: Math.max(0.3 * v, floor),
  };
  if (v <= floor * 2 || n < 4) return moment;

  // empirical variogram, 6 bins over [0, max(0.6·diam, 2·mind)]
  const maxlag = Math.max(0.6 * diam, mind * 2);
  const BINS = 6;
  const bw = maxlag / BINS;
  const gSum = new Float64Array(BINS);
  const gCnt = new Int32Array(BINS);
  for (let i = 0; i < n; i++)
    for (let j = i + 1; j < n; j++) {
      const h = Math.sqrt((x[i]! - x[j]!) ** 2 + (y[i]! - y[j]!) ** 2);
      if (h >= maxlag) continue; // h=0 pairs belong to bin 0, exactly as gs.vario_estimate bins them
      const b = Math.min(BINS - 1, Math.floor(h / bw));
      const d = val[i]! - val[j]!;
      gSum[b] += 0.5 * d * d;
      gCnt[b]++;
    }
  const bc: number[] = [];
  const gamma: number[] = [];
  for (let b = 0; b < BINS; b++) {
    if (gCnt[b]! > 0 && gSum[b]! / gCnt[b]! > 0) {
      bc.push((b + 0.5) * bw);
      gamma.push(gSum[b]! / gCnt[b]!);
    }
  }
  if (bc.length < 3) return moment;

  // deterministic bounded search: sill FIXED at v (so nugget + partial sill = v), γ(h) = nugget +
  // (v−nugget)·(1−exp(−h/ℓ)). 48 log-spaced ℓ × 25 nugget levels — exhaustive beats a browser LM.
  const loL = mind * 0.25;
  const hiL = diam * 1.5;
  let best: Variogram | null = null;
  let bestErr = Infinity;
  for (let li = 0; li < 48; li++) {
    const l = loL * (hiL / loL) ** (li / 47);
    for (let ni = 0; ni < 25; ni++) {
      const ng = (v * ni) / 24;
      let err = 0;
      for (let b = 0; b < bc.length; b++) {
        const g = ng + (v - ng) * (1 - Math.exp(-bc[b]! / l));
        // soft_l1, f_scale=1 — gstools' fit_variogram default loss; plain SSE lets one outlier bin
        // drag the K-in-ppm fits far from that criterion
        const r = g - gamma[b]!;
        err += 2 * (Math.sqrt(1 + r * r) - 1);
      }
      if (err < bestErr) {
        bestErr = err;
        best = { sill: v - ng, lenScale: l, nugget: ng };
      }
    }
  }
  // compute's own acceptance test: an insane fit falls back to the moment estimate
  if (
    !best ||
    !Number.isFinite(best.lenScale) ||
    best.lenScale < mind * 0.1 ||
    best.lenScale > diam * 3 ||
    best.sill < 0
  )
    return moment;
  return best;
}

// NO nugget here — gstools puts the nugget ONLY on the kriging-matrix diagonal (cond_err), never in
// off-diagonal or prediction covariances.
const cov = (h: number, vm: Variogram): number =>
  vm.sill * Math.exp(-h / vm.lenScale);

/*
 *
 * DUAL-FORM kriging: factor the (n + constraints)² system once, solve α once, then each target costs
 * O(n). `drift` null = Ordinary (Lagrange row only); non-null = KED (drift row too — gstools
 * ExtDrift's local mean a + b·drift). Returns null when the system is singular (a discarded bag).
 */
export function krigeDual(
  sx: Float64Array,
  sy: Float64Array,
  val: Float64Array,
  vm: Variogram,
  drift: Float64Array | null,
  gx: Float64Array,
  gy: Float64Array,
  gdrift: Float64Array | null,
): Float64Array | null {
  const n = sx.length;
  // a (near-)constant drift column is exactly collinear with the Lagrange ones row — singular no
  // matter how the covariance block is regularised. It also carries no information beyond the mean
  // so KED with constant drift IS ordinary kriging; degrading to OK is what gstools' pseudo-inverse
  // resolves the redundancy to in effect.
  if (drift) {
    let lo = Infinity;
    let hi = -Infinity;
    for (let i = 0; i < n; i++) {
      const d = drift[i]!;
      if (d < lo) lo = d;
      if (d > hi) hi = d;
    }
    if (!(hi - lo > 1e-9 * (1 + Math.max(Math.abs(lo), Math.abs(hi))))) {
      drift = null;
      gdrift = null;
    }
  }
  const dim = n + 1 + (drift ? 1 : 0);
  const build = (jitter: number): Float64Array => {
    const M = new Float64Array(dim * dim);
    for (let i = 0; i < n; i++)
      for (let j = 0; j < n; j++) {
        const h =
          i === j
            ? 0
            : Math.sqrt((sx[i]! - sx[j]!) ** 2 + (sy[i]! - sy[j]!) ** 2);
        // nugget on the DIAGONAL only (see `cov`); `jitter` is the pseudo-inverse stand-in below
        M[i * dim + j] = cov(h, vm) + (i === j ? vm.nugget + jitter : 0);
      }
    for (let i = 0; i < n; i++) {
      M[i * dim + n] = 1;
      M[n * dim + i] = 1;
    }
    if (drift)
      for (let i = 0; i < n; i++) {
        M[i * dim + n + 1] = drift[i]!;
        M[(n + 1) * dim + i] = drift[i]!;
      }
    return M;
  };
  // production solves even a singular system (gstools defaults pseudo_inv=True, so "every draw
  // singular → RFRK fallback" almost never fires there). A full SVD pinv is out of scope for the
  // browser; a tiny diagonal jitter retry is the standing stand-in — it regularises the same systems
  // (collinear drift, duplicate rows) into solvable ones with a bias far below the data's noise.
  let alpha: Float64Array | null = null;
  for (const jitter of [0, (vm.sill + vm.nugget) * 1e-8]) {
    const A = build(jitter);
    const { piv, singular } = luFactor(A, dim);
    if (singular) continue;
    const rhs = new Float64Array(dim);
    for (let i = 0; i < n; i++) rhs[i] = val[i]!;
    const x = new Float64Array(dim);
    luSolve(A, piv, dim, rhs, x);
    if (x.every((a) => Number.isFinite(a))) {
      alpha = x;
      break;
    }
  }
  if (!alpha) return null;

  const m = gx.length;
  const out = new Float64Array(m);
  const sill = vm.sill;
  const inv = 1 / vm.lenScale;
  for (let c = 0; c < m; c++) {
    let v = alpha[n]!; // constant term
    if (drift && gdrift) v += alpha[n + 1]! * gdrift[c]!;
    const cx = gx[c]!;
    const cy = gy[c]!;
    for (let i = 0; i < n; i++) {
      const dx = sx[i]! - cx;
      const dy = sy[i]! - cy;
      v += alpha[i]! * (sill * Math.exp(-Math.sqrt(dx * dx + dy * dy) * inv));
    }
    out[c] = v;
  }
  return out;
}

/*
 *
 * SIMPLE kriging — a KNOWN mean, no Lagrange constraint: α = C⁻¹(val − mean), prediction =
 * mean + Σ αᵢ·c(h). gstools' `krige.Simple(mean=…)` semantics; far from the data it reverts to the
 * given mean (its signature difference from OK, which drifts to a locally-estimated one). Same
 * dual form, nugget-on-diagonal, and jitter-retry discipline as `krigeDual`.
 */
export function simpleKrige(
  sx: Float64Array,
  sy: Float64Array,
  val: Float64Array,
  vm: Variogram,
  mean: number,
  gx: Float64Array,
  gy: Float64Array,
): Float64Array | null {
  const n = sx.length;
  const build = (jitter: number): Float64Array => {
    const M = new Float64Array(n * n);
    for (let i = 0; i < n; i++)
      for (let j = 0; j < n; j++) {
        const h =
          i === j
            ? 0
            : Math.sqrt((sx[i]! - sx[j]!) ** 2 + (sy[i]! - sy[j]!) ** 2);
        M[i * n + j] = cov(h, vm) + (i === j ? vm.nugget + jitter : 0);
      }
    return M;
  };
  let alpha: Float64Array | null = null;
  for (const jitter of [0, (vm.sill + vm.nugget) * 1e-8]) {
    const A = build(jitter);
    const { piv, singular } = luFactor(A, n);
    if (singular) continue;
    const rhs = new Float64Array(n);
    for (let i = 0; i < n; i++) rhs[i] = val[i]! - mean;
    const x = new Float64Array(n);
    luSolve(A, piv, n, rhs, x);
    if (x.every((a) => Number.isFinite(a))) {
      alpha = x;
      break;
    }
  }
  if (!alpha) return null;
  const m = gx.length;
  const out = new Float64Array(m);
  const sill = vm.sill;
  const inv = 1 / vm.lenScale;
  for (let c = 0; c < m; c++) {
    let v = mean;
    const cx = gx[c]!;
    const cy = gy[c]!;
    for (let i = 0; i < n; i++) {
      const dx = sx[i]! - cx;
      const dy = sy[i]! - cy;
      v += alpha[i]! * (sill * Math.exp(-Math.sqrt(dx * dx + dy * dy) * inv));
    }
    out[c] = v;
  }
  return out;
}

/*
 *
 * RFRK_OK — `residual_kriging`: trend(grid) + OK(residuals)(grid).
 */
export function rfrkOk(
  sx: Float64Array,
  sy: Float64Array,
  resid: Float64Array,
  gx: Float64Array,
  gy: Float64Array,
  gtrend: Float64Array,
): Float64Array | null {
  const vm = fitVariogramRobust(sx, sy, resid);
  const kriged = krigeDual(sx, sy, resid, vm, null, gx, gy, null);
  // a singular solve is NULL, not an all-NaN surface — an all-NaN array is truthy, and the caller
  // once mistook it for success and rendered an empty pane labeled "recreated live" (review
  // finding).
  if (!kriged) return null;
  const out = new Float64Array(gx.length);
  for (let c = 0; c < gx.length; c++) {
    const t = gtrend[c]!;
    out[c] = Number.isFinite(t) ? t + kriged[c]! : Number.NaN;
  }
  return out;
}

export interface EbkOptions {
  nBag?: number;
  subsetFrac?: number;
  seed?: number;
}

export interface EbkDraw {
  /* * the bag's KED surface (the fitted-drift estimator) — null when its solve was degenerate */
  ked: Float64Array | null;
  /*
   *
   * the SAME bag's ordinary-kriging surface, same variogram, drift ignored — the μ=0 endpoint of
   * the model-influence family: kriging is linear in its data, so surface(μ) = (1−μ)·OK + μ·KED IS
   * the fixed-μ·b̂ partial-drift estimator, per bag.
   */
  ok: Float64Array | null;
}

/*
 *
 * Bagged EBK-KED, one DRAW per generator step — `ebk_ked`'s loop, yieldable so a caller can hand the
 * event loop back between bags (a Pi-class machine takes seconds on a 640-ac field; a frozen spinner
 * would lie about it). Each draw: random 80% subset, variogram REFIT on that subset's residuals (the
 * "empirical Bayesian" part), then BOTH estimators over that subset+variogram: KED with the trend as
 * external drift, and its OK twin. Consume with `ebkKed` or iterate.
 */
export function* ebkKedDraws(
  sx: Float64Array,
  sy: Float64Array,
  obs: Float64Array,
  drift: Float64Array,
  gx: Float64Array,
  gy: Float64Array,
  gdrift: Float64Array,
  opts: EbkOptions = {},
): Generator<EbkDraw, void> {
  const n = sx.length;
  const nBag = opts.nBag ?? 20;
  const m = Math.min(Math.max(4, Math.floor((opts.subsetFrac ?? 0.8) * n)), n); // int truncates in the reference
  const rnd = mulberry32(opts.seed ?? 0);
  for (let b = 0; b < nBag; b++) {
    const idx =
      m < n
        ? sampleWithoutReplacement(rnd, n, m)
        : Int32Array.from({ length: n }, (_, i) => i);
    const bx = new Float64Array(m);
    const by = new Float64Array(m);
    const bo = new Float64Array(m);
    const bd = new Float64Array(m);
    const br = new Float64Array(m);
    for (let i = 0; i < m; i++) {
      const k = idx[i]!;
      bx[i] = sx[k]!;
      by[i] = sy[k]!;
      bo[i] = obs[k]!;
      bd[i] = drift[k]!;
      br[i] = obs[k]! - drift[k]!;
    }
    const vm = fitVariogramRobust(bx, by, br);
    const ked = krigeDual(bx, by, bo, vm, bd, gx, gy, gdrift);
    const ok = krigeDual(bx, by, bo, vm, null, gx, gy, null);
    yield {
      ked: ked && ked.every((v) => Number.isFinite(v)) ? ked : null,
      ok: ok && ok.every((v) => Number.isFinite(v)) ? ok : null,
    };
  }
}

/*
 * The full bagged average — `ebk_ked`'s return, one mean per estimator: `ked` null if every KED
 * draw was singular (compute falls back to RFRK_OK native there, and so must the caller); `ok`
 * null if every OK twin failed (in practice: never when any KED solved). Counts are independent —
 * a bag whose KED is degenerate can still contribute its OK.
 */
export function ebkKed(
  sx: Float64Array,
  sy: Float64Array,
  obs: Float64Array,
  drift: Float64Array,
  gx: Float64Array,
  gy: Float64Array,
  gdrift: Float64Array,
  opts: EbkOptions = {},
): { ked: Float64Array | null; ok: Float64Array | null } {
  const kedAcc = new Float64Array(gx.length);
  const okAcc = new Float64Array(gx.length);
  let kedCnt = 0;
  let okCnt = 0;
  for (const draw of ebkKedDraws(sx, sy, obs, drift, gx, gy, gdrift, opts)) {
    if (draw.ked) {
      for (let c = 0; c < draw.ked.length; c++) kedAcc[c] += draw.ked[c]!;
      kedCnt++;
    }
    if (draw.ok) {
      for (let c = 0; c < draw.ok.length; c++) okAcc[c] += draw.ok[c]!;
      okCnt++;
    }
  }
  if (kedCnt > 0) for (let c = 0; c < kedAcc.length; c++) kedAcc[c] /= kedCnt;
  if (okCnt > 0) for (let c = 0; c < okAcc.length; c++) okAcc[c] /= okCnt;
  return { ked: kedCnt > 0 ? kedAcc : null, ok: okCnt > 0 ? okAcc : null };
}

// ---- kriging spaces + physical bounds (infer.py's tables, verbatim) -------------------------------
/*
 * log1p for the right-skewed concentrations (clipped ≥0 first — an edge-extrapolated negative trend
 * must not NaN); native for pH (log of a pH is chemically meaningless) and K.
 */
export const KRIGING_SPACE: Record<string, "log" | "native"> = {
  p: "log",
  cec: "log",
  om: "log",
  ph: "native",
  k: "native",
};
export const KRIGING_METHOD: Record<string, "ebk" | "rfrk_ok"> = {
  ph: "ebk",
  p: "ebk",
  cec: "ebk",
  om: "ebk",
  k: "rfrk_ok",
};
/*
 * clamp the final surface into physical range (BOUNDS in properties.py) — extrapolation past the
 * sample hull can go negative at a field edge.
 */
export const KRIGING_BOUNDS: Record<string, [number, number]> = {
  ph: [3, 10],
  om: [0, Infinity],
  cec: [0, Infinity],
  p: [0, Infinity],
  k: [0, Infinity],
};

export const fwdLog = (v: number): number => Math.log1p(Math.max(v, 0));
export const invLog = (v: number): number => Math.expm1(v);
